import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Option } from './Option';
import { RawInput } from '../InputText/RawInput';
import { Icon } from '../Icon';
import * as Styles from './index.styles';
import { useClickOutside } from '../../Utils/Hooks';
import { ARROW_KEY_DOWN, ARROW_KEY_UP, ENTER_KEY, TAB_KEY } from './index.constants';
import { normalizeAccents } from '../../Utils/Normalizers';

const { StyledSelect, StyledOptions, StyledSelectWrapper } = Styles;

export const Select = ({
    onlySelect,
    className,
    name,
    defaultValue,
    options: rawOptions,
    fullWidth,
    onChange,
    onFocus,
    onBlur,
    suggest,
    suggestLimit,
    noHover,
    disabled,
    isClear,
    inputRef: selectRef,
    ...otherProps
}) => {
    const [selectedValue, setSelectedValue] = useState(defaultValue);
    const [isOpen, setIsOpen] = useState(false);
    const [options, setOptions] = useState(rawOptions);
    const [dirty, setDirty] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [tabKeyPressed, setTabKeyPressed] = useState(false);
    const wrapperRef = React.createRef();
    const inputRef = selectRef || React.createRef();
    const styledOptionsRef = React.createRef();
    const optionsRef =
        rawOptions && rawOptions.length ? rawOptions.map(() => React.createRef()) : [];

    const selectedIndexOverflows = highlightedIndex > suggestLimit - 1;

    const onOptionClick = (value, id) => {
        setSelectedValue(value);
        onChange(value, id);
        setIsOpen(false);
    };

    const onInputChange = evt => {
        setSelectedValue(evt.target.value);
        onChange(evt.target.value);
        setDirty(true);
    };

    /* instanbul ignore next */
    const getHeightMap = () => {
        return optionsRef.map((optionRef, i) => {
            if (optionRef.current && i < highlightedIndex) {
                return optionRef.current.clientHeight;
            }

            return 0;
        });
    };

    /* istanbul ignore next */
    const highlightAndScrollToCurrentOption = () => {
        const heightMap = getHeightMap();
        const totalScroll = heightMap.reduce((total, height) => total + height, 0);
        const optionOffset = optionsRef[highlightedIndex].current.offsetTop;
        const visibleOffsetStart = styledOptionsRef.current.scrollTop;
        const visibleOffsetEnd =
            styledOptionsRef.current.scrollTop + styledOptionsRef.current.offsetHeight;
        const optionOverflowsVisibleStartOffset = optionOffset <= visibleOffsetStart;
        const optionOverflowsVisibleEndOffset = optionOffset >= visibleOffsetEnd;

        if (optionOverflowsVisibleStartOffset || optionOverflowsVisibleEndOffset) {
            styledOptionsRef.current.scrollTop = totalScroll;
        }
    };

    const dispatchBlurEvent = evt => {
        onBlur(evt);
        setDirty(false);
        setIsOpen(false);
        setTabKeyPressed(false);

        setTimeout(() => {
            setOptions(rawOptions);
        }, 300);
    };

    const dispatchKeyDownEvent = evt => {
        if (otherProps.onKeyDown) {
            otherProps.onKeyDown(evt);
        }
    };

    const checkForTabKeyPressed = keyCode => {
        setTabKeyPressed(keyCode === TAB_KEY);
    };

    useClickOutside(wrapperRef, () => {
        if (isOpen) {
            setIsOpen(false);
        }
    });

    React.useEffect(() => {
        if (suggest && !dirty) {
            return;
        }

        const rawOptionsNormalized = rawOptions.map(option => ({
            value: option.value || '',
            valueNormalized: normalizeAccents(option.value || '').toLowerCase(),
            id: option.id,
        }));

        const selectedValueNormalized = normalizeAccents(selectedValue || '').toLowerCase();

        const parsedOptions = suggest
            ? rawOptionsNormalized.filter(option =>
                  option.valueNormalized.includes(selectedValueNormalized),
              )
            : rawOptionsNormalized.filter(
                  option => option.valueNormalized !== selectedValueNormalized,
              );

        setOptions(parsedOptions);
        setHighlightedIndex(0);
    }, [rawOptions, selectedValue, suggest, suggestLimit]);

    React.useEffect(() => {
        if (!isOpen) {
            setHighlightedIndex(0);
        }
    }, [isOpen]);

    /* istanbul ignore next */
    React.useEffect(() => {
        if (!styledOptionsRef.current) {
            return;
        }

        if (!selectedIndexOverflows) {
            styledOptionsRef.current.scrollTop = 0;
            return;
        }

        const hasRefForOptionIndex =
            optionsRef[highlightedIndex] && optionsRef[highlightedIndex].current;

        if (hasRefForOptionIndex) {
            highlightAndScrollToCurrentOption();
        }
    }, [highlightedIndex, optionsRef]);

    const onInputBlur = evt => {
        evt.persist();
        const shouldDispatchBlur = !isOpen || tabKeyPressed;

        if (shouldDispatchBlur) {
            dispatchBlurEvent(evt);
            return;
        }

        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleSelectClick = () => {
        setIsOpen(!disabled && !isOpen);

        if (inputRef.current && !isOpen) {
            inputRef.current.focus();
        }
    };

    const handleKeyDown = evt => {
        evt.persist();

        if (isOpen) {
            if (evt.keyCode === ARROW_KEY_UP && highlightedIndex > 0) {
                setHighlightedIndex(highlightedIndex - 1);
            }

            if (evt.keyCode === ARROW_KEY_DOWN && highlightedIndex < options.length - 1) {
                setHighlightedIndex(highlightedIndex + 1);
            }

            if (evt.keyCode === ENTER_KEY) {
                const selected = options[highlightedIndex];

                setDirty(true);
                onOptionClick(selected.value, selected.id);
            }

            if (evt.keyCode === TAB_KEY) {
                const selected = options[highlightedIndex];

                setDirty(true);
                onOptionClick(selected.value, selected.id);
            }

            checkForTabKeyPressed();
        }

        if (!isOpen) {
            setIsOpen(true);
            setHighlightedIndex(0);
        }

        checkForTabKeyPressed(evt.keyCode);
        dispatchKeyDownEvent(evt);
    };

    const cleanOtherProps = { ...otherProps };
    delete cleanOtherProps.onKeyDown;
    delete cleanOtherProps.onBlur;

    return (
        <StyledSelectWrapper ref={wrapperRef} fullWidth={fullWidth}>
            <StyledSelect
                className={className}
                fullWidth={fullWidth}
                hasSuggest={!!suggest}
                disabled={disabled}
                onClick={handleSelectClick}
            >
                <RawInput
                    isClear={isClear}
                    name={name}
                    value={selectedValue}
                    inputRef={inputRef}
                    icon={<Icon icon={['far', 'chevron-down']} />}
                    fullWidth={fullWidth}
                    onChange={!onlySelect ? onInputChange : () => {}}
                    autoComplete="off"
                    disabled={disabled}
                    onFocus={onFocus}
                    onBlur={onInputBlur}
                    onKeyDown={handleKeyDown}
                    {...cleanOtherProps}
                >
                    {isOpen && (
                        <StyledOptions
                            ref={styledOptionsRef}
                            suggestLimit={suggestLimit}
                            noHover={noHover}
                        >
                            {options.length ? (
                                options.map((option, index) => (
                                    <Option
                                        onClick={evt => {
                                            evt.stopPropagation();
                                            onOptionClick(option.value, option.id);
                                        }}
                                        key={option.id.toString()}
                                        value={option.id}
                                        selected={highlightedIndex === index}
                                        ref={optionsRef[index]}
                                    >
                                        {option.value}
                                    </Option>
                                ))
                            ) : (
                                <Option value="">Nenhuma opção disponível</Option>
                            )}
                        </StyledOptions>
                    )}
                </RawInput>
            </StyledSelect>
        </StyledSelectWrapper>
    );
};

Select.propTypes = {
    name: PropTypes.string,
    fullWidth: PropTypes.bool,
    suggest: PropTypes.bool,
    suggestLimit: PropTypes.number,
    noHover: PropTypes.bool,
    disabled: PropTypes.bool,
    defaultValue: PropTypes.string,
    className: PropTypes.string,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onlySelect: PropTypes.bool,
    inputRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    ]),
    options: PropTypes.arrayOf(
        PropTypes.shape({ name: PropTypes.string, value: PropTypes.string }),
    ),
    isClear: PropTypes.bool,
};

Select.defaultProps = {
    name: '',
    fullWidth: false,
    suggest: false,
    suggestLimit: null,
    noHover: false,
    disabled: false,
    defaultValue: '',
    className: '',
    onChange: () => null,
    onFocus: () => null,
    onBlur: () => null,
    inputRef: null,
    options: [],
    onlySelect: false,
    isClear: false,
};

Select.Styles = Styles;
